var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * CZNA-407 — puts NK's help wording in front of the packaged translations.
 *
 * A nested `IntlProvider` inherits every setting of the Stripes one and changes only
 * `messages`, so `HelpTooltip`, `PaneHelpButton`, `FieldHelpRow`, `FormHelp` and every other
 * `FormattedMessage` keep calling react-intl exactly as before — they do not know overrides
 * exist. Without a usable override the provider passes the parent's own `messages` object
 * through, so help renders byte for byte as it did before (pinned by
 * `HelpTextProvider.test.tsx`).
 *
 * The provider always renders, overrides or not: swapping it in once the request answers
 * would remount the whole module tree and drop every open form's state.
 *
 * The context exposes the packaged messages to the Settings editor, which needs the
 * defaults the override layer hides.
 */
import React, { createContext, useContext, useMemo } from 'react';
import { IntlProvider, useIntl } from 'react-intl';
import useHelpTexts, { useSubfieldDescriptions } from '../hooks/useHelpTexts';
import { usableOverrides } from '../utils/helpTextOverrides';
var HelpTextContext = createContext({
    baseMessages: {},
    storedOverrides: {},
    subfieldDescriptions: {},
});
export var useHelpTextContext = function () { return useContext(HelpTextContext); };
var HelpTextProvider = function (_a) {
    var children = _a.children;
    var intl = useIntl();
    var storedOverrides = useHelpTexts();
    var subfieldDescriptions = useSubfieldDescriptions();
    var baseMessages = intl.messages;
    var messages = useMemo(function () {
        var usable = usableOverrides(storedOverrides, baseMessages, intl);
        return Object.keys(usable).length === 0
            ? intl.messages
            : __assign(__assign({}, intl.messages), usable);
        // `intl` is recreated with its config; the inputs that matter are listed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storedOverrides, baseMessages, intl.locale]);
    var context = useMemo(function () { return ({ baseMessages: baseMessages, storedOverrides: storedOverrides, subfieldDescriptions: subfieldDescriptions }); }, [baseMessages, storedOverrides, subfieldDescriptions]);
    return (React.createElement(HelpTextContext.Provider, { value: context },
        React.createElement(IntlProvider, { locale: intl.locale, timeZone: intl.timeZone, formats: intl.formats, defaultLocale: intl.defaultLocale, defaultFormats: intl.defaultFormats, textComponent: intl.textComponent, onError: intl.onError, onWarn: intl.onWarn, wrapRichTextChunksInFragment: intl.wrapRichTextChunksInFragment, defaultRichTextElements: intl.defaultRichTextElements, fallbackOnEmptyString: intl.fallbackOnEmptyString, messages: messages }, children)));
};
export default HelpTextProvider;
